import { set } from 'vue-demi';
import { Sessions } from '@or-sdk/hitl';

import { HITL } from '@/api';
import { useSessionsStore, useSessionsSetters } from './use-sessions-store';
import { useSessionViews } from '@/modules/session-views-store';
import { useContacts } from '@/modules/contacts-store';
import { modify, SEARCH_SESSIONS_CHUNK_SIZE, CONTACT_HISTORY_STATUS } from './common';
import { THROW_API_ERROR, UIEventBus } from '@/common/ui-event-bus';

export const useSessionsGetActions = () => {
  const store = useSessionsStore();
  const { upsert } = useSessionsSetters();
  const { activeConversationFilter } = useSessionViews();
  const { getAPIContacts } = useContacts();

  const getAPISessionInitTriggers = async () => {
    try {
      const triggers = await HITL.sessions.getSessionInitTriggers();
      store.conversationTriggers = triggers;
      return triggers;
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      return [];
    }
  };

  const getAPISessionById = async (args: {
    sessionId: string;
    save?: boolean;
  }) => {
    try {
      const { sessionId, save = true } = args;
      const response = await HITL.sessions.getSessionById({ sessionId });
      const session = modify(response);

      if (save) {
        upsert(session);
      }

      return session;
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
      throw error;
    }
  };

  /** TODO: Add pagination! */
  const getAPISessionsByContactId = async (args: { contactId: string; }) => {
    const { contactId } = args;

    try {
      set(store.historyStatus, contactId, CONTACT_HISTORY_STATUS.LOADING);
      const response = await HITL.sessions.getSessions({ contactId });
      const sessions = modify(response.data);

      upsert(sessions);
      set(store.historyStatus, contactId, CONTACT_HISTORY_STATUS.READY);
      return sessions;
    } catch (e) {
      set(store.historyStatus, contactId, CONTACT_HISTORY_STATUS.NONE);
      UIEventBus.$emit(THROW_API_ERROR, e);
    }
  };

  /**
   * Search sessions while applying active sessions filter.
   * Sessions are requested in chunks and contacts are request as well.
   */
  const searchAPISessionsInChunks = async (args: { reset?: boolean; } = {}) => {
    store.pagination.isLoading = true;
    if (args.reset) {
      store.pagination.nextId = '';
      store.pagination.current = 0;
    }

    try {
      const response = await HITL.sessions.searchSessions({
        sessionsSearchOptions: {
          offset: store.pagination.current,
          limit: SEARCH_SESSIONS_CHUNK_SIZE,
        },
        ...activeConversationFilter.value,
      });

      store.pagination.nextId = response.page?.nextId || '';
      store.pagination.current += SEARCH_SESSIONS_CHUNK_SIZE;
      upsert(modify(response.data));

      const contactIds = response.data.map(i => i.contactId);
      await getAPIContacts({ contactIds });
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    } finally {
      store.pagination.isLoading = false;
    }
  };

  return {
    getAPISessionInitTriggers,
    getAPISessionById,
    getAPISessionsByContactId,
    searchAPISessionsInChunks,
  };
};
